import { useCallback, useEffect, useState } from 'react'
import { bettingApi } from '../../api'
import { ErrorState, errorMessage } from '../ErrorState'
import type { DiscoveryTerm, DiscoveryTermKind, TermYield, VocabularyYield } from '../../types'
import { Block, SidePanel, ViewTabs } from '../../ui/ui'
import { useI18n, useT } from '../../i18n'
import type { MessageKey, TFunction } from '../../i18n'

/**
 * Vocabulaire de la recherche de sites de paris.
 *
 * Ce que l'agent écrit ici décide de ce que le robot cherche et de ce qu'il
 * retient. Les termes sont cherchés tels quels, jamais comme des expressions
 * régulières : la parenthèse d'un « pari (gratuit) » reste une parenthèse.
 */

/**
 * Le jeton de pays reste `{pays}` : c'est ce que le serveur remplace dans une
 * requête, et le traduire casserait les termes déjà enregistrés.
 */
const COUNTRY_TOKEN = '{pays}'

const TERM_KINDS: Array<{ value: DiscoveryTermKind; labelKey: MessageKey; hintKey: MessageKey }> = [
  { value: 'QUERY', labelKey: 'bet.termKind.QUERY', hintKey: 'bet.termKind.QUERY.hint' },
  { value: 'BETTING_SIGNAL', labelKey: 'bet.termKind.BETTING_SIGNAL', hintKey: 'bet.termKind.BETTING_SIGNAL.hint' },
  { value: 'REVIEW_SIGNAL', labelKey: 'bet.termKind.REVIEW_SIGNAL', hintKey: 'bet.termKind.REVIEW_SIGNAL.hint' },
  { value: 'NOISE_DOMAIN', labelKey: 'bet.termKind.NOISE_DOMAIN', hintKey: 'bet.termKind.NOISE_DOMAIN.hint' },
]

function kindHint(t: TFunction, kind: DiscoveryTermKind): string {
  const found = TERM_KINDS.find((k) => k.value === kind)
  return found ? t(found.hintKey, { token: COUNTRY_TOKEN }) : ''
}

const splitList = (s: string) =>
  s
    .split(/[,|\n]/)
    .map((v) => v.trim())
    .filter(Boolean)

/**
 * Rendement d'un terme, tel qu'il se lit dans le tableau.
 *
 * Un taux sans décisions derrière lui n'est pas une mesure : il s'affiche alors
 * comme une absence, et le nombre de jugements accompagne toujours le taux pour
 * qu'un « 100 % » sur une seule décision ne se lise pas comme un résultat.
 */
function Rendement({ stat }: { stat: TermYield | undefined }) {
  const t = useT()
  if (!stat || stat.surfaced === 0) {
    return <span className="bet-muted">{t('bet.yieldNothing')}</span>
  }
  if (stat.successRate === null) {
    return <span className="bet-muted">{t('bet.yieldNoneJudged', { n: stat.surfaced })}</span>
  }
  return (
    <>
      {t('bet.yieldRate', {
        rate: Math.round(stat.successRate * 100),
        imported: stat.imported,
        decided: stat.decided,
        pending: stat.pending > 0 ? t('bet.yieldPending', { n: stat.pending }) : '',
      })}
    </>
  )
}

export function BettingDiscoveryTerms({ canManage }: { canManage: boolean }) {
  const { t } = useI18n()
  const [terms, setTerms] = useState<DiscoveryTerm[]>([])
  const [kind, setKind] = useState<DiscoveryTermKind>('QUERY')
  const [editing, setEditing] = useState<DiscoveryTerm | 'new' | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * Rendement du vocabulaire, tous pays confondus.
   *
   * Son absence n'est pas une panne : tant qu'aucune passe n'a été jugée, il n'y
   * a rien à mesurer. L'écran reste donc utilisable sans lui.
   */
  const [rendement, setRendement] = useState<VocabularyYield | null>(null)

  const load = useCallback(() => {
    bettingApi
      .listDiscoveryTerms()
      .then(setTerms)
      .catch((err) => setError(errorMessage(err)))
    bettingApi
      .vocabularyYield()
      .then(setRendement)
      .catch(() => setRendement(null))
  }, [])
  useEffect(load, [load])

  const statPour = (terme: DiscoveryTerm): TermYield | undefined =>
    rendement?.terms.find((t) => t.kind === terme.kind && t.value === terme.value)
  /** Les domaines écartés ne remontent rien : la colonne n'aurait aucun sens. */
  const montreRendement = kind !== 'NOISE_DOMAIN' && rendement !== null

  const shown = terms.filter((t) => t.kind === kind)
  const count = (k: DiscoveryTermKind) => terms.filter((t) => t.kind === k).length
  const activeSignals = terms.filter((t) => t.kind === 'BETTING_SIGNAL' && t.isActive).length
  const activeQueries = terms.filter((t) => t.kind === 'QUERY' && t.isActive).length

  const remove = async (term: DiscoveryTerm) => {
    setError(null)
    try {
      await bettingApi.deleteDiscoveryTerm(term.id)
      setEditing(null)
      load()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <Block
      flush
      title={t('bet.termsTitle')}
      subtitle={t('bet.termsSub', { n: terms.length, queries: activeQueries })}
      action={
        canManage ? (
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            {t('bet.addTerm')}
          </button>
        ) : undefined
      }
    >
      <div className="bet-block-tabs">
        <ViewTabs
          label={t('bet.termFamily')}
          value={kind}
          onChange={setKind}
          options={TERM_KINDS.map((k) => ({
            value: k.value,
            label: t('bet.termKindCount', { label: t(k.labelKey), n: count(k.value) }),
          }))}
        />
        <p className="bet-hint">{kindHint(t, kind)}</p>
        {montreRendement && rendement && (
          <p className="bet-hint">
            {t('bet.yieldExplain', {
              candidates: rendement.totals.candidates,
              pending: rendement.totals.pending,
              noReason:
                rendement.totals.rejectedWithoutReason > 0
                  ? t('bet.yieldNoReason', { n: rendement.totals.rejectedWithoutReason })
                  : '',
            })}
          </p>
        )}
      </div>
      {activeSignals === 0 && (
        <p className="bet-notice" role="status">{t('bet.noActiveSignal')}</p>
      )}
      {error && <ErrorState message={error} compact />}
      <div className="table-scroll">
        <table className="bet-table">
          <thead>
            <tr>
              <th scope="col">{t('bet.colTerm')}</th>
              <th scope="col">{t('common.country')}</th>
              <th scope="col">{t('bet.colState')}</th>
              {montreRendement && <th scope="col">{t('bet.colYield')}</th>}
              <th scope="col">{t('bet.colNote')}</th>
              {canManage && (
                <th scope="col">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {shown.map((term) => (
              <tr key={term.id}>
                <td className="bet-mono">{term.value}</td>
                <td>{term.countries.length ? term.countries.join(', ') : <span className="bet-muted">{t('bet.allCountriesShort')}</span>}</td>
                <td>{term.isActive ? t('bet.active') : <span className="bet-muted">{t('bet.inactive')}</span>}</td>
                {montreRendement && (
                  <td>
                    <Rendement stat={statPour(term)} />
                  </td>
                )}
                <td>{term.notes || <span className="bet-muted">—</span>}</td>
                {canManage && (
                  <td>
                    <button type="button" className="btn btn-secondary" onClick={() => setEditing(term)}>
                      {t('common.edit')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {shown.length === 0 && (
              <tr>
                <td colSpan={(canManage ? 5 : 4) + (montreRendement ? 1 : 0)} className="bet-muted">
                  {t('bet.noTermInFamily')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && (
        <DiscoveryTermDialog
          term={editing === 'new' ? null : editing}
          defaultKind={kind}
          onDelete={remove}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
    </Block>
  )
}

function DiscoveryTermDialog({
  term,
  defaultKind,
  onDelete,
  onClose,
  onSaved,
}: {
  term: DiscoveryTerm | null
  defaultKind: DiscoveryTermKind
  onDelete: (term: DiscoveryTerm) => void
  onClose: () => void
  onSaved: () => void
}) {
  const t = useT()
  const [kind, setKind] = useState<DiscoveryTermKind>(term?.kind ?? defaultKind)
  const [value, setValue] = useState(term?.value ?? '')
  const [countries, setCountries] = useState((term?.countries ?? []).join(', '))
  const [isActive, setIsActive] = useState(term?.isActive ?? true)
  const [notes, setNotes] = useState(term?.notes ?? '')
  const [error, setError] = useState<string | null>(null)

  const placeholder =
    kind === 'QUERY'
      ? t('bet.termQueryPlaceholder', { token: COUNTRY_TOKEN })
      : kind === 'NOISE_DOMAIN'
        ? t('bet.termNoisePlaceholder')
        : t('bet.termSignalPlaceholder')

  const submit = async () => {
    setError(null)
    const payload = {
      kind,
      value: value.trim(),
      countries: splitList(countries).map((c) => c.toUpperCase()),
      isActive,
      notes: notes.trim() || null,
    }
    try {
      if (term) await bettingApi.updateDiscoveryTerm(term.id, payload)
      else await bettingApi.createDiscoveryTerm(payload)
      onSaved()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <SidePanel
      title={term ? t('bet.termFormEdit', { value: term.value }) : t('bet.termFormNew')}
      onClose={onClose}
      footer={
        <>
          {term && (
            <button type="button" className="btn btn-secondary bet-footer-left" onClick={() => onDelete(term)}>
              {t('common.delete')}
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" disabled={value.trim().length < 2} onClick={submit}>
            {t('common.save')}
          </button>
        </>
      }
    >
      <div className="bet-form">
        <label>
          <span>{t('bet.family')}</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as DiscoveryTermKind)}>
            {TERM_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {t(k.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <p className="bet-hint">{kindHint(t, kind)}</p>
        <label>
          <span>{kind === 'NOISE_DOMAIN' ? t('bet.rootDomain') : t('bet.term')}</span>
          <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} />
        </label>
        <p className="bet-hint">{t('bet.termLiteralHint')}</p>
        <label>
          <span>{t('bet.termCountriesField')}</span>
          <input value={countries} onChange={(e) => setCountries(e.target.value)} placeholder="SN, CI" />
        </label>
        <label className="bet-check">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span>{t('bet.active')}</span>
        </label>
        <label>
          <span>{t('bet.colNote')}</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        {error && <ErrorState message={error} compact />}
      </div>
    </SidePanel>
  )
}
